/**
 * Words for the roster-fix flow. Strategies return data; this turns it into
 * sentences in the reader's language, so neither side knows about the other.
 *
 * Callers pass the hook's `t`/`tn` so the dialog re-renders on a language
 * change. Every key is passed to `t` as a string literal, never through a
 * lookup table, because the i18n coverage scan only sees literals.
 */

import type { TranslateVars } from '../../../i18n';
import type {
  ComplianceIssue,
  FixOption,
  FixVerification,
  RankedCandidate,
  RosterChangeOperation,
  RosterRuleViolation,
  RosterSnapshot,
  UnsupportedReason,
} from '../../../services/rosterCompliance';
import { formatAbsoluteMinute, MINUTES_PER_DAY } from '../../../utils/siteScheduling';

export type Translate = (text: string, vars?: TranslateVars) => string;
export type TranslatePlural = (singular: string, plural: string, count: number, vars?: TranslateVars) => string;

export interface Translator {
  t: Translate;
  tn: TranslatePlural;
}

/** 07:30, or 02:00 (+1) for time that runs into the next day. */
export function formatClock(minute: number): string {
  if (minute === MINUTES_PER_DAY) return '24:00';
  const time = formatAbsoluteMinute(minute);
  return minute > MINUTES_PER_DAY ? `${time} (+1)` : time;
}

export function formatRange(startMinute: number, endMinute: number): string {
  return `${formatClock(startMinute)}–${formatClock(endMinute)}`;
}

export function formatDuration(minutes: number, { t }: Translator): string {
  const rounded = Math.max(0, Math.round(minutes));
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  if (hours === 0) return t('{m}m', { m: rest });
  if (rest === 0) return t('{h}h', { h: hours });
  return t('{h}h {m}m', { h: hours, m: rest });
}

export function ruleTitle(violation: RosterRuleViolation, { t }: Translator): string {
  switch (violation.rule_code) {
    case 'break_allowance': return t('Break too short');
    case 'minimum_rest_between_shifts': return t('Not enough rest between shifts');
    case 'max_shift_length': return t('Shift too long');
    case 'night_work_shift_limit': return t('Night shift too long');
    case 'max_consecutive_work_days': return t('Too many days in a row');
    case 'max_average_weekly_hours': return t('Average weekly hours over the limit');
    case 'night_work_average_weekly_hours': return t('Night-work weekly average over the limit');
    case 'night_work_average_24h_hours': return t('Night-work daily average over the limit');
    default: return violation.rule_name;
  }
}

export function guardLabel(violation: RosterRuleViolation, { t }: Translator): string {
  return violation.guard_name?.trim() || t('Unknown guard');
}

/** The first official source the rule row cites, if any. */
export function regulationUrl(violation: RosterRuleViolation): string | null {
  const urls = violation.details.source_urls;
  if (!Array.isArray(urls)) return null;
  const url = urls.find((value): value is string => typeof value === 'string' && /^https:\/\//.test(value));
  return url ?? null;
}

const candidateName = (option: FixOption, tr: Translator): string => (
  option.candidate?.guard.fullName ?? tr.t('Unknown guard')
);

export function fixTitle(option: FixOption, tr: Translator): string {
  const { t } = tr;
  const range = formatRange(option.window.startMinute, option.window.endMinute);
  const time = option.newBoundaryMinute !== null ? formatClock(option.newBoundaryMinute) : '';
  const guard = option.guardName || t('Unknown guard');

  switch (option.kind) {
    case 'add_break':
      return t('Add a {duration} break at {range}', { duration: formatDuration(option.window.endMinute - option.window.startMinute, tr), range });
    case 'replace_break':
      return t('Replace it with one {duration} break at {range}', { duration: formatDuration(option.window.endMinute - option.window.startMinute, tr), range });
    case 'reassign_shift':
      return t('Give the shift to {guard}', { guard: candidateName(option, tr) });
    case 'handover_start':
      return t('{guard} starts at {time}; {other} covers before', { guard, time, other: candidateName(option, tr) });
    case 'handover_end':
      return t('{guard} finishes at {time}; {other} takes over', { guard, time, other: candidateName(option, tr) });
    case 'trim_start':
      return t('{guard} starts later, at {time}', { guard, time });
    case 'trim_end':
      return t('{guard} finishes earlier, at {time}', { guard, time });
  }
}

export function fixSummary(option: FixOption, tr: Translator): string {
  const { t } = tr;
  const range = formatRange(option.window.startMinute, option.window.endMinute);
  const guard = option.guardName || t('Unknown guard');

  switch (option.kind) {
    case 'add_break':
      return t('Placed mid-shift and clear of colleagues on the same post, so it stays covered.');
    case 'replace_break':
      return t('One uninterrupted break instead of the shorter one rostered — some rules require it unbroken.');
    case 'reassign_shift':
      return t('{other} works {range} instead of {guard}.', { other: candidateName(option, tr), range, guard });
    case 'handover_start':
    case 'handover_end':
      return t('{other} covers {range}.', { other: candidateName(option, tr), range });
    case 'trim_start':
    case 'trim_end':
      return t('{range} is left uncovered on the board.', { range });
  }
}

export type BadgeTone = 'success' | 'warning' | 'neutral';

export function verificationBadge(verification: FixVerification, target: RosterRuleViolation, tr: Translator): { tone: BadgeTone; text: string } {
  const { t, tn } = tr;
  if (verification.status === 'resolves_with_new') {
    return {
      tone: 'warning',
      text: tn('Fixes this, but adds {count} new breach', 'Fixes this, but adds {count} new breaches', verification.introduced.length),
    };
  }
  if (verification.status === 'resolves') {
    const others = verification.resolved.filter((violation) => violation !== target && violation.message !== target.message).length;
    return {
      tone: 'success',
      text: others > 0
        ? tn('Fixes this and {count} other breach', 'Fixes this and {count} other breaches', others)
        : t('Fixes this breach'),
    };
  }
  return { tone: 'neutral', text: t('Does not fix this breach') };
}

/** Short reasons this guard was suggested, best first. */
export function candidateNotes(candidate: RankedCandidate, tr: Translator): string[] {
  const { t, tn } = tr;
  return candidate.highlights.flatMap((note): string[] => {
    const value = note.value ?? 0;
    switch (note.code) {
      case 'preferred': return [t('Prefers this time')];
      case 'available': return [t('Marked available')];
      case 'site_familiar': return [tn('Worked this site {count} time recently', 'Worked this site {count} times recently', value)];
      case 'week_hours': return [value > 0
        ? t('{hours} rostered this week', { hours: formatDuration(value, tr) })
        : t('Nothing else rostered this week')];
      case 'rest_before': return [t('{hours} rest before', { hours: formatDuration(value, tr) })];
      case 'rest_after': return [t('{hours} rest after', { hours: formatDuration(value, tr) })];
      case 'extends_shift': return [t('Joins onto a {hours} shift', { hours: formatDuration(value, tr) })];
      default: return [];
    }
  });
}

export function unsupportedText(reason: UnsupportedReason, { t }: Translator): string {
  switch (reason) {
    case 'no_strategy': return t('There is no automatic fix for this rule yet. Adjust the roster on the board.');
    case 'shift_not_on_roster': return t('The shifts involved are on another roster. Open that site and day to fix it there.');
    case 'no_eligible_guards': return t('No guard can lawfully take this work. Adjust the roster on the board.');
    case 'no_verified_fix': return t('None of the possible changes passes the rules. Adjust the roster on the board.');
  }
}

export interface OperationLookup {
  snapshot: RosterSnapshot;
  guardName: (guardId: string) => string;
}

/** One line per change, in terms of duties and people rather than ids. */
export function describeOperation(operation: RosterChangeOperation, lookup: OperationLookup, tr: Translator): string {
  const { t } = tr;
  const name = lookup.guardName;
  const duty = (id: string) => lookup.snapshot.slots.find((slot) => slot.id === id);
  const breakSlot = (id: string) => lookup.snapshot.breakSlots.find((slot) => slot.id === id);

  switch (operation.op) {
    case 'reassign_duty': {
      const slot = duty(operation.duty_slot_id);
      return t('{to} takes {duty} ({range}) from {from}', {
        to: name(operation.to_guard_id),
        from: name(operation.from_guard_id),
        duty: slot?.name ?? t('a duty'),
        range: slot ? formatRange(slot.start_minute, slot.end_minute) : '',
      });
    }
    case 'remove_duty': {
      const slot = duty(operation.duty_slot_id);
      return t('{guard} comes off {duty} ({range}); the place is left open', {
        guard: name(operation.guard_id),
        duty: slot?.name ?? t('a duty'),
        range: slot ? formatRange(slot.start_minute, slot.end_minute) : '',
      });
    }
    case 'split_duty': {
      const slot = duty(operation.duty_slot_id);
      const start = slot?.start_minute ?? operation.at_minute;
      const end = slot?.end_minute ?? operation.at_minute;
      const keepsBefore = operation.guard_keeps === 'before';
      const kept = keepsBefore ? formatRange(start, operation.at_minute) : formatRange(operation.at_minute, end);
      const released = keepsBefore ? formatRange(operation.at_minute, end) : formatRange(start, operation.at_minute);
      const vars = {
        duty: slot?.name ?? t('a duty'),
        time: formatClock(operation.at_minute),
        guard: name(operation.guard_id),
        kept,
        released,
      };
      return operation.handover_guard_id
        ? t('{duty} splits at {time}: {guard} keeps {kept}, {other} covers {released}', { ...vars, other: name(operation.handover_guard_id) })
        : t('{duty} splits at {time}: {guard} keeps {kept}, {released} is left open', vars);
    }
    case 'add_break': {
      const vars = {
        guard: name(operation.guard_id),
        duration: formatDuration(operation.end_minute - operation.start_minute, tr),
        range: formatRange(operation.start_minute, operation.end_minute),
      };
      return operation.is_paid
        ? t('{guard} gets a {duration} paid break at {range}', vars)
        : t('{guard} gets a {duration} break at {range}', vars);
    }
    case 'transfer_break': {
      const slot = breakSlot(operation.break_slot_id);
      return t('The break at {range} moves from {from} to {to}', {
        range: slot ? formatRange(slot.start_minute, slot.end_minute) : '',
        from: name(operation.from_guard_id),
        to: name(operation.to_guard_id),
      });
    }
    case 'remove_break': {
      const slot = breakSlot(operation.break_slot_id);
      return t('{guard} no longer takes the break at {range}', {
        guard: name(operation.guard_id),
        range: slot ? formatRange(slot.start_minute, slot.end_minute) : '',
      });
    }
  }
}

/** Short label for a breach in lists and notices: "Shift too long (Aoife Byrne)". */
export function issueLabel(issue: ComplianceIssue, tr: Translator): string {
  return tr.t('{rule} ({guard})', { rule: ruleTitle(issue.violation, tr), guard: guardLabel(issue.violation, tr) });
}
